'use client';

import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import PlayIcon from '@fe-news/components/atoms/PlayButton/PlayButton';
import { NewsCoverHeight } from '@fe-news/constants/cover';
import { styled } from '@linaria/react';
import Image from 'next/image';
import type { FC } from 'react';
import { memo, useState } from 'react';

interface INewsCoverVideoProps {
  youtubeId: string;
  coverSrc: string;
  isAutoPlay?: boolean;
}

const YOUTUBE_EMBED_URL = 'https://www.youtube.com/embed/';
const YOUTUBE_PLAY_PARAMS = '?autoplay=1&rel=0&mute=1';

const VideoContainer = styled.div`
  position: relative;
  height: ${NewsCoverHeight.DESKTOP}px;
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  border-radius: 6px;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    height: ${NewsCoverHeight.TABLET}px;
  }

  @media screen and (max-width: ${SCREEN_SIZE.MOBILE + 1}px) {
    height: ${NewsCoverHeight.MOBILE}px;
  }
`;

const CoverImage = styled(Image)`
  position: absolute;
  object-fit: cover;
  border-radius: 6px;
  z-index: 1;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
`;

const DEFAULT_PLAY_BUTTON_SIZE = 64;

const NewsCoverVideo: FC<INewsCoverVideoProps> = ({ coverSrc, youtubeId, isAutoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(isAutoPlay);

  const isEmpty = !coverSrc || !youtubeId;

  if (isEmpty) return null;

  const onClickPlayHandler = () => {
    setIsPlaying(true);
  };

  return (
    <VideoContainer onClick={onClickPlayHandler}>
      {isPlaying ? (
        <iframe
          title='video'
          src={`${YOUTUBE_EMBED_URL}${youtubeId}${YOUTUBE_PLAY_PARAMS}`}
          width='100%'
          height='100%'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
          allowFullScreen
        />
      ) : (
        <>
          <CoverImage
            priority
            alt='cover image of news article'
            src={coverSrc}
            fill
            sizes={`
              (max-width: ${SCREEN_SIZE.DESKTOP}px) 100% ${NewsCoverHeight.TABLET}px,
              (max-width: ${SCREEN_SIZE.MOBILE}px) 100% ${NewsCoverHeight.MOBILE}px
            `}
          />
          <PlayButton key='playButton'>
            <PlayIcon size={DEFAULT_PLAY_BUTTON_SIZE} />
          </PlayButton>
        </>
      )}
    </VideoContainer>
  );
};

export default memo(NewsCoverVideo);
